import React, { useState } from 'react';
import api from '../../utils/api';
import { Button, Card } from '../admin/ui';
import { useToast } from '../admin/overlays';
import { IconCourier, IconLock, IconRefresh } from '../icons';
import { formatNumber } from '../admin/labels';

/** Windows every courier page offers. The server clamps to 1–365 regardless. */
export const COURIER_WINDOWS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' }
];

export const WindowPicker = ({ days, onChange, onRefresh, loading }) => (
  <div className="flex items-center gap-2">
    <div className="inline-flex gap-1 rounded-xl bg-ink-100 p-1" role="group" aria-label="Time window">
      {COURIER_WINDOWS.map((w) => (
        <button
          key={w.days}
          type="button"
          onClick={() => onChange(w.days)}
          aria-pressed={days === w.days}
          className={`rounded-lg px-3 py-1.5 text-xs font-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-600 ${
            days === w.days ? 'bg-white text-info-700 shadow-card' : 'text-ink-600 hover:text-ink-900'
          }`}
        >
          {w.label}
        </button>
      ))}
    </div>
    {onRefresh && (
      <Button variant="secondary" size="sm" onClick={onRefresh} loading={loading}>
        <IconRefresh size={15} />
        Refresh
      </Button>
    )}
  </div>
);

/**
 * What a pilot sees where an Intelligence page would be.
 *
 * Starts the checkout from here rather than linking away: the person is
 * already signed in and has just been told what they are missing, so sending
 * them to a pricing page to start again would lose them.
 */
export const UpgradeGate = ({ title, description, features = [], price, onUpgraded }) => {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const startUpgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/api/courier-portal/plan-checkout');
      if (data?.url) {
        onUpgraded?.();
        window.location.href = data.url;
        return;
      }
      toast('Could not start checkout. Please try again.', { tone: 'error' });
    } catch (error) {
      toast(error.response?.data?.error || 'Could not start checkout. Please try again.', {
        tone: 'error'
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-col items-start gap-6 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-info-50 text-info-600">
            <IconLock size={20} />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-[15px] font-700 text-ink-900">{title}</h2>
            <p className="mt-1 text-sm leading-relaxed text-ink-600">{description}</p>
            {features.length > 0 && (
              <ul className="mt-3 grid gap-x-5 gap-y-1.5 sm:grid-cols-2">
                {features.map((feature) => (
                  <li key={feature} className="flex items-start gap-1.5 text-sm text-ink-700">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-info-500" />
                    {feature}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <Button variant="info" className="shrink-0" loading={busy} disabled={busy} onClick={startUpgrade}>
          <IconCourier size={16} />
          {price != null ? `Subscribe — £${formatNumber(price)}/month` : 'Subscribe to Intelligence'}
        </Button>
      </div>
    </Card>
  );
};

/**
 * A day-by-day area chart, drawn as inline SVG.
 *
 * No charting library: one series of plain numbers does not justify 50kB of
 * JavaScript, and the external-script rules make a CDN build a non-starter.
 * The path is built from a viewBox-relative scale so it stays sharp at any
 * width without measuring the DOM.
 */
export const TrendChart = ({ points = [], height = 160, tone = 'info' }) => {
  const colors = {
    info: { stroke: 'stroke-info-600', fill: 'fill-info-500/15', dot: 'fill-info-600' },
    brand: { stroke: 'stroke-brand-700', fill: 'fill-brand-600/15', dot: 'fill-brand-700' }
  }[tone];

  if (points.length === 0) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-ink-500">
        Nothing reported in this period.
      </div>
    );
  }

  const W = 600;
  const H = 180;
  const pad = 4;
  const max = Math.max(...points.map((p) => p.count), 1);
  const stepX = points.length > 1 ? (W - pad * 2) / (points.length - 1) : 0;
  const x = (i) => pad + i * stepX;
  const y = (count) => H - pad - (count / max) * (H - pad * 2);

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.count).toFixed(1)}`).join(' ');
  const area = `${line} L ${x(points.length - 1).toFixed(1)} ${H - pad} L ${x(0).toFixed(1)} ${H - pad} Z`;

  const peak = points.reduce((best, p, i) => (p.count > points[best].count ? i : best), 0);
  const first = points[0];
  const last = points[points.length - 1];

  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        style={{ height }}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Reports per day. Peak ${points[peak].count} on ${points[peak].date}.`}
      >
        <path d={area} className={`${colors.fill} stroke-none`} />
        <path d={line} className={`${colors.stroke} fill-none`} strokeWidth="2" vectorEffect="non-scaling-stroke" />
        {/* Only the peak is marked: a dot per day is noise at 90 days. */}
        <circle cx={x(peak)} cy={y(points[peak].count)} r="3" className={colors.dot} vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="mt-2 flex items-center justify-between text-[11px] text-ink-500">
        <span>{first?.date}</span>
        <span className="font-600 text-ink-700">
          Peak {points[peak].count} on {points[peak].date}
        </span>
        <span>{last?.date}</span>
      </div>
    </div>
  );
};
